import React, { useContext, useRef } from 'react';
import { Button, Upload, List, Typography, Tooltip, Tag } from 'antd';
import { GoogleOutlined, LoadingOutlined, DeleteOutlined, PaperClipOutlined, UploadOutlined, EyeOutlined, WarningOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './index.scss'; // We'll create this later if needed, or reuse SingleUpload's SCSS if applicable
import SettingContext from '../../contexts/settingProvider';

const { Dragger } = Upload;
const { Text } = Typography; // Added for upload hint

const MultiUpload = ({
  files = [], // Array of file objects
  isUploading, // General uploading state for any file
  onFileAdd, // Callback for local file additions: (file) => void
  onGoogleDriveClick, // Callback to trigger Google Drive picker
  onFileDelete, // Callback to delete a file: (file) => void
  acceptTypes,
  disabled,
  googleDriveEnabled = true,
  maxFiles, // Optional: maximum number of files allowed
}) => {
  const { dark } = useContext(SettingContext);
  const { t } = useTranslation();
  const uploadRef = useRef(null);

  const handleLocalFileChange = (info) => {
    // Ant Design's Upload component with `maxCount` should internally handle
    // not processing files that exceed the maxCount limit, or it will mark them with an error status.
    // We call onFileAdd only if the file is not in an error state.
    if (info.file && info.file.status !== 'error') {
      // The `files.length < (maxFiles || Infinity)` check that was previously here
      // is now better handled by antd's `maxCount` prop on the Upload component itself,
      // especially for a batch of simultaneously selected files.
      // The parent component (`onFileAdd` -> `handleAddAttachmentFile`) will manage its own list.
      onFileAdd(info.file); 
    } else if (info.file && info.file.status === 'error') {
      // Ant Design might have already displayed a message (e.g., for maxCount violation).
      // You can add custom logging or user feedback here if needed.
      console.warn(`Antd Upload error for file ${info.file.name}:`, info.file.error || 'Exceeded max file count or other error.');
    }
  };

  const draggerProps = {
    name: 'file',
    multiple: true,
    accept: acceptTypes,
    disabled: disabled || isUploading || (maxFiles && files.length >= maxFiles),
    beforeUpload: () => false,
    onChange: handleLocalFileChange,
    showUploadList: false,
    maxCount: maxFiles,
  };

  const handleSelectFileClick = () => {
    if (uploadRef.current && uploadRef.current.upload && uploadRef.current.upload.uploader) {
      const uploader = uploadRef.current.upload.uploader;
      if (typeof uploader.onClick === 'function') {
        const inputEl = uploader.fileInput || uploader.inputRef?.current || uploader.input;
        try {
          uploader.onClick({ target: inputEl || {} });
        } catch (error) {
          console.error('Error calling Antd uploader.onClick in MultiUpload:', error, uploader);
          if (inputEl && typeof inputEl.click === 'function') {
            inputEl.click();
          } else {
            const draggerInputElement = uploadRef.current.querySelector('input[type="file"]');
            draggerInputElement?.click();
          }
        }
      } else {
        console.error('Antd uploader.onClick is not a function in MultiUpload', uploader);
      }
    } else {
      console.error('Could not trigger Antd Upload click programmatically in MultiUpload (ref structure issue)', uploadRef.current);
    }
  };

  return (
    <div className={`multiupload-section ${dark ? 'dark' : ''}`}>
      <div className="multiupload-content-area">
        <Dragger {...draggerProps} ref={uploadRef} className={`multi-custom-dragger ${files.length > 0 ? 'has-files' : 'no-files'}`}>
          {files.length === 0 ? (
            isUploading ? (
              <div className="dragger-content dragger-loading-content">
                <LoadingOutlined className="dragger-icon-large"/>
                <p className="ant-upload-text">{t('chatInput.multiUpload.uploading')}</p>
              </div>
            ) : (
              <div className="dragger-content dragger-empty-content">
                <p className="ant-upload-text">{t('chatInput.multiUpload.dropzoneText')}</p>
              </div>
            )
          ) : ( 
            <List
              className="embedded-file-list"
              header={<Text strong>{t('chatInput.multiUpload.selectedFiles')} ({files.length}{maxFiles ? `/${maxFiles}` : ''})</Text>}
              itemLayout="horizontal"
              dataSource={files}
              renderItem={(file, index) => (
                <List.Item
                  key={file.id || file.uid || file.source_name || index}
                  actions={[
                    file.source_source === 'drive' && (file.url || file.source_url) && (
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(file.url || file.source_url, '_blank', 'noopener,noreferrer');
                        }}
                        disabled={disabled || isUploading}
                        style={{ paddingLeft: '8px', paddingRight: '8px' }}
                      >
                        {t('chatInput.multiUpload.view')}
                      </Button>
                    ),
                    !isUploading || !file.isUploadingThisSpecificFile ? (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onFileDelete(file);
                        }}
                        disabled={disabled || isUploading}
                      />
                    ) : (
                      <LoadingOutlined />
                    )
                  ]}
                >
                  <List.Item.Meta
                    avatar={<PaperClipOutlined />}
                    title={
                      <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '4px' }}>
                        <Tooltip title={file.source_name || file.name} placement="topLeft">
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1', minWidth: 0 }}>
                            {file.source_name || file.name}
                          </span>
                        </Tooltip>
                        {file.isFromPreviousMeeting && (
                          <Tooltip title={t('chatInput.multiUpload.fromPreviousMeeting', { date: file.previousMeetingDate })}>
                            <Tag color="blue" size="small" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '2px' }}>
                              <HistoryOutlined style={{ fontSize: '10px' }} />
                              {t('chatInput.multiUpload.previousMeetingTag')}
                            </Tag>
                          </Tooltip>
                        )}
                        {file.hasPermission === false && (
                          <Tooltip title={t('chatInput.multiUpload.permissionWarning')}>
                            <WarningOutlined style={{ color: 'orange', marginLeft: '8px', flexShrink: 0 }} />
                          </Tooltip>
                        )}
                      </span>
                    }
                    description={file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : ''}
                  />
                </List.Item>
              )}
            />
          )}
        </Dragger>

        <div className="multiupload-side-actions">
          <Button 
            icon={<UploadOutlined />} 
            onClick={handleSelectFileClick} 
            disabled={disabled || isUploading || (maxFiles && files.length >= maxFiles)}
            className="select-file-button-multi"
          >
            {t('chatInput.multiUpload.selectFile')}
          </Button>
          {googleDriveEnabled && (
            <Button
              icon={<GoogleOutlined />}
              onClick={onGoogleDriveClick}
              disabled={disabled || isUploading || (maxFiles && files.length >= maxFiles)}
              className="google-drive-button-multi"
            >
              {t('chatInput.multiUpload.googleDrive')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiUpload; 